import React from 'react';
import { 
  TrendingUp, 
  DollarSign, 
  MapPin, 
  Package,
  Gauge 
} from 'lucide-react';
import type { KPIData } from '../types';

interface KPICardsProps {
  data: KPIData;
  isLoading?: boolean;
}

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'info';
  isLoading?: boolean;
  trend?: {
    value: number;
    isUp: boolean;
  };
}

function KPICard({ title, value, subtitle, icon, color, isLoading, trend }: KPICardProps) {
  if (isLoading) {
    return (
      <div className="kpi-card animate-pulse">
        <div className="skeleton h-6 w-20 mb-2"></div>
        <div className="skeleton h-10 w-32 mb-2"></div>
        <div className="skeleton h-4 w-24"></div>
      </div>
    );
  }

  const colorClasses = {
    primary: 'text-netflix-red',
    success: 'text-yuli-green',
    warning: 'text-yuli-orange',
    info: 'text-yuli-blue',
  };

  return (
    <div className="kpi-card group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-netflix-grey-400 mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-netflix-grey-400">{subtitle}</p>
          )}
          
          {/* Trend indicator */}
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${
              trend.isUp ? 'text-yuli-green' : 'text-red-400'
            }`}>
              <TrendingUp 
                className={`w-4 h-4 mr-1 ${trend.isUp ? '' : 'rotate-180'}`}
              />
              <span>{Math.abs(trend.value)}% vs last period</span>
            </div>
          )}
        </div>
        
        <div className={`p-3 rounded-xl bg-netflix-grey-600 group-hover:bg-netflix-grey-500 transition-colors ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function KPICards({ data, isLoading = false }: KPICardsProps) {
  // Format numbers for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatRPM = (value: number) => {
    return `$${value.toFixed(2)}`;
  };

  const kpis = [
    {
      title: 'Weighted RPM',
      value: isLoading ? '' : formatRPM(data.weighted_rpm),
      subtitle: 'Revenue per mile',
      icon: <Gauge className="w-6 h-6" />,
      color: 'primary' as const,
    },
    {
      title: 'Total Revenue',
      value: isLoading ? '' : formatCurrency(data.total_revenue),
      subtitle: 'Billable amount',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'success' as const,
    },
    {
      title: 'Total Miles',
      value: isLoading ? '' : formatNumber(data.total_miles),
      subtitle: 'Miles driven',
      icon: <MapPin className="w-6 h-6" />,
      color: 'info' as const,
    },
    {
      title: 'Load Count',
      value: isLoading ? '' : formatNumber(data.load_count),
      subtitle: 'Loads completed',
      icon: <Package className="w-6 h-6" />,
      color: 'warning' as const,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
      {kpis.map((kpi, index) => (
        <KPICard
          key={index}
          {...kpi}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
}